import { FeatureGateTier, HexId, SalesUpsellFeatureGates } from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { CheckoutStep } from "../../components/checkout/checkoutConstants";
import { OnboardingDialogId } from "../../components/tour/dialogs";

export type ContactSalesVariant =
  | SalesUpsellFeatureGates
  | "CTA_AI_CREDITS_OVERVIEW"
  | "CTA_PRICING_PAGE"
  | "CTA_TRIAL_BANNER";

/**
 * Optional args when opening the plans modal to preselect a tier and/or initial step
 */
export type PlansModalArgs = {
  tier?: FeatureGateTier;
  initialStep?: CheckoutStep;
};

type UiSliceState = {
  contactSalesFormOpen: ContactSalesVariant | null;
  plansModalOpen: boolean;
  plansModalArgs: PlansModalArgs | null;
  /** Only switched to true on successful checkout */
  plansModalCheckoutComplete: boolean;
  /**
   * True when checkout succeeded but the seat/role updates could not be applied.
   */
  plansModalSeatUpdateFailed: boolean;
  onboardingDialog: {
    id: OnboardingDialogId;
    hexId: HexId;
    properties?: {
      context?: "skipped-ai";
    };
  } | null;
};

const initialState: UiSliceState = {
  contactSalesFormOpen: null,
  plansModalOpen: false,
  plansModalArgs: null,
  plansModalCheckoutComplete: false,
  plansModalSeatUpdateFailed: false,
  onboardingDialog: null,
};

const UiSlice = createSlice({
  name: "Ui",
  initialState,
  reducers: {
    setContactSalesFormOpen: (
      state,
      action: PayloadAction<ContactSalesVariant>,
    ) => {
      state.contactSalesFormOpen = action.payload;
    },
    closeContactSalesForm: (state) => {
      state.contactSalesFormOpen = null;
    },
    openPlansModal: (
      state,
      action: PayloadAction<PlansModalArgs | undefined>,
    ) => {
      state.plansModalOpen = true;
      state.plansModalArgs = action.payload ?? null;
      state.plansModalCheckoutComplete = false;
      state.plansModalSeatUpdateFailed = false;
    },
    closePlansModal: (state) => {
      state.plansModalOpen = false;
      state.plansModalArgs = null;
      state.plansModalCheckoutComplete = false;
      state.plansModalSeatUpdateFailed = false;
    },
    markPlansCheckoutComplete: (
      state,
      action: PayloadAction<{ seatUpdateFailed: boolean } | undefined>,
    ) => {
      state.plansModalCheckoutComplete = true;
      state.plansModalSeatUpdateFailed =
        action.payload?.seatUpdateFailed ?? false;
    },
    setOnboardingDialog: (
      state,
      action: PayloadAction<NonNullable<UiSliceState["onboardingDialog"]>>,
    ) => {
      state.onboardingDialog = action.payload;
    },
    closeOnboardingDialog: (state) => {
      state.onboardingDialog = null;
    },
  },
});

export const uiReducer = UiSlice.reducer;
export const {
  closeContactSalesForm,
  closeOnboardingDialog,
  closePlansModal,
  markPlansCheckoutComplete,
  openPlansModal,
  setContactSalesFormOpen,
  setOnboardingDialog,
} = UiSlice.actions;

export const selectUi = (state: { ui: UiSliceState }): UiSliceState => state.ui;
